import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { RecordPicker } from '../../components/RecordPicker.js';

/**
 * M10 L10.10 — Material Validation (§13).
 *
 * Lists the materials the painter recorded for the review's job
 * (`GET /v1/quality/reviews/:reviewId/materials`), each with the quantity he
 * consumed and the decision recorded on it. The reviewer names a material,
 * gives the quantity he approves and decides via
 * `POST /v1/quality/reviews/:reviewId/materials`; the material and the quantity
 * consumed are the painter's own record, not typed here. An override needs
 * remarks.
 */
type MaterialDecision = 'APPROVED' | 'REJECTED' | 'OVERRIDE';

interface SubmissionMaterial {
  readonly executionMaterialId: string;
  readonly materialCode: string;
  readonly consumedQty: number | null;
  readonly uomCode: string | null;
  readonly capturedAt: string;
  readonly materialValidationId: string | null;
  readonly approvedQty: number | null;
  readonly decision: MaterialDecision | null;
}

export function QualityMaterialValidationPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const materialsPath = `/v1/quality/reviews/${reviewId}/materials`;
  const quantity = (qty: number | null, uom: string | null): string =>
    qty === null ? decimal(qty) : `${decimal(qty)} ${unitLabel(uom ?? '')}`.trim();
  const materialLabel = (m: SubmissionMaterial): string =>
    [m.materialCode, quantity(m.consumedQty, m.uomCode)].join(' · ');
  const [rows, setRows] = useState<readonly SubmissionMaterial[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [executionMaterialId, setExecutionMaterialId] = useState('');
  const [approvedQty, setApprovedQty] = useState('');
  const [decision, setDecision] = useState<MaterialDecision>('APPROVED');
  const [reviewRemarks, setReviewRemarks] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<readonly SubmissionMaterial[]>(materialsPath, { token });
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [materialsPath, token]);

  useEffect(() => { void load(); }, [load]);

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(materialsPath, {
        method: 'POST', token,
        body: {
          executionMaterialId,
          approvedQty: approvedQty.trim() === '' ? undefined : Number(approvedQty),
          decision,
          reviewRemarks: reviewRemarks.trim() || undefined,
        },
      });
      setExecutionMaterialId(''); setApprovedQty(''); setDecision('APPROVED'); setReviewRemarks('');
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [materialsPath, token, executionMaterialId, approvedQty, decision, reviewRemarks, load]);

  const remarksMissing = decision === 'OVERRIDE' && !reviewRemarks.trim();
  const inputStyle = { padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' };

  return (
    <main aria-label="quality-material-validation">
      <p style={{ marginBottom: 8 }}><Link to={`/quality/review/${reviewId}`}>{t('quality.common.back')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.material.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <form onSubmit={onSubmit} aria-label="material-decision-form" style={{ maxWidth: 520, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <RecordPicker<SubmissionMaterial>
          endpoint={materialsPath}
          idField="executionMaterialId"
          label={t('quality.material.col.material')}
          testId="mat-id"
          value={executionMaterialId}
          optionLabel={materialLabel}
          onPick={setExecutionMaterialId}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="mat-approved" style={{ fontSize: 12 }}>{t('quality.material.f.approved')}</label>
          <input id="mat-approved" data-testid="mat-approved" type="number" value={approvedQty}
                 onChange={(e) => setApprovedQty(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="mat-decision" style={{ fontSize: 12 }}>{t('quality.common.decision')}</label>
          <select id="mat-decision" data-testid="mat-decision" value={decision}
                  onChange={(e) => setDecision(e.target.value as MaterialDecision)} style={inputStyle}>
            <option value="APPROVED">{t('quality.common.approve')}</option>
            <option value="REJECTED">{t('quality.common.reject')}</option>
            <option value="OVERRIDE">{t('quality.material.opt.override')}</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="mat-remarks" style={{ fontSize: 12 }}>{t('quality.common.remarks')}</label>
          <textarea id="mat-remarks" data-testid="mat-remarks" value={reviewRemarks} rows={2}
                    onChange={(e) => setReviewRemarks(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <Button type="submit" data-testid="mat-submit" disabled={busy || !executionMaterialId || remarksMissing} variant="primary">
            {busy ? t('quality.common.saving') : t('quality.common.save')}
          </Button>
          {actionError && <p role="alert" style={{ color: 'var(--color-danger)', marginTop: 8 }}>{actionError}</p>}
        </div>
      </form>

      <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.material.recorded')}</h2>
      {rows.length === 0 ? (
        <p data-testid="mat-empty" style={{ color: 'var(--color-text-muted)' }}>{t('quality.material.empty')}</p>
      ) : (
        <DataTable<SubmissionMaterial>
          testId="mat-table"
          rowKey={(r) => r.executionMaterialId}
          rowTestId={(r) => `mat-row-${r.executionMaterialId}`}
          columns={[
            { key: 'material', header: t('quality.material.col.material'), render: (r) => r.materialCode },
            { key: 'consumed', header: t('quality.material.col.consumed'), render: (r) => quantity(r.consumedQty, r.uomCode) },
            { key: 'approved', header: t('quality.material.col.approved'), render: (r) => quantity(r.approvedQty, r.uomCode) },
            { key: 'decision', header: t('quality.common.decision'), render: (r) => r.decision ?? '—' },
          ]}
          rows={rows}
        />
      )}
    </main>
  );
}